import { Logo } from '../ui'
import {
  useState,
  useEffect,
} from 'react'
import { useAuth } from '../../contexts/AuthContext/AuthContext'
import { useMutation } from '@apollo/client'
import { LOGIN_MUTATION } from '../../graphql/mutations/auth'
import { useNavigate } from 'react-router-dom'
import { ROOT_PATH } from '../../config/rootPath'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [loginMutation, { loading: mutationLoading }] = useMutation(LOGIN_MUTATION)
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [isValid, setIsValid] = useState(true)
  const [authError, setAuthError] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')

  useEffect(() => {
    const checkAutofill = () => {
      const emailInput = document.getElementById('email') as HTMLInputElement
      const passwordInput = document.getElementById('password') as HTMLInputElement
      
      if (emailInput && passwordInput) {
        if (emailInput.value && passwordInput.value) {
          setFormData({
            email: emailInput.value,
            password: passwordInput.value
          })
          setTimeout(validateForm, 100)
        }
      }
    }

    checkAutofill()
    const timer = setTimeout(checkAutofill, 100)
    
    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName === 'onAutoFillStart') {
        checkAutofill()
      }
    }
    
    document.addEventListener('animationstart', handleAnimationStart)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('animationstart', handleAnimationStart)
    }
  }, [])

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password: string) => {
    return password.length >= 6
  }

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {}
    
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    
    if (formData.password && !validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    setErrors(newErrors)
    setIsValid(Object.keys(newErrors).length === 0)
  }

  const handleInputChange = (field: 'email' | 'password', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
    setAuthError('')
    setTimeout(validateForm, 0)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError('')
    setSuccessMessage('')
    
    try {
      const { data } = await loginMutation({
        variables: {
          email: formData.email,
          password: formData.password
        }
      })
      
      if (data?.userLogin?.errors) {
        setAuthError(data.userLogin.errors[0] || 'Invalid credentials')
      } else if (data?.userLogin?.user && data?.userLogin?.token) {
        login(data.userLogin.user, data.userLogin.token)
        setSuccessMessage('Welcome back!')
        setTimeout(() => {
          navigate('/dashboard')
        }, 1000)
      }
    } catch (error) {
      setAuthError('Network error. Please try again.')
    }
  }

  return (
    <>
      <style>
        {`
          @keyframes onAutoFillStart {
            from {/**/}
            to {/**/}
          }
          
          @keyframes onAutoFillCancel {
            from {/**/}
            to {/**/}
          }
          
          input:-webkit-autofill {
            animation-name: onAutoFillStart;
          }
          
          input:not(:-webkit-autofill) {
            animation-name: onAutoFillCancel;
          }
        `}
      </style>
      <div className="relative flex min-h-screen shrink-0 justify-center md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-white px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <main className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            <div className="flex">
              <a aria-label="Home" href={ROOT_PATH}>
                <Logo variant="image" showText={true} size="md" />
              </a>
          </div>
          <h2 className="mt-20 text-lg font-semibold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-sm text-gray-700">
            Don't have an account?{' '}
            <a className="font-medium text-blue-600 hover:underline" href="/register">
              Sign up
            </a>{' '}
            for a free trial.
          </p>
          <form className="mt-10 grid grid-cols-1 gap-y-8" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="mb-3 block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                name="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="mb-3 block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                name="password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
            <div className="text-right">
              <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                Forgot your password?
              </a>
            </div>
            {authError && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-600">{authError}</p>
              </div>
            )}
            {successMessage && (
              <div className="rounded-md bg-green-50 p-4">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}
            <div>
              <button
                className="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"
                disabled={!isValid || mutationLoading}
              >
                <span>
                  {mutationLoading ? 'Signing in...' : 'Sign in'} <span aria-hidden="true">→</span>
                </span>
              </button>
            </div>
          </form>
        </main>
      </div>
      <div className="hidden sm:contents lg:relative lg:block lg:flex-1">
        <img
          alt=""
          loading="lazy"
          width="1664"
          height="1866"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ color: 'transparent' }}
          src="/background-auth.jpg"
        />
      </div>
    </div>
    </>
  )
} 